import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Info, CheckCircle2, AlertTriangle, AlertCircle, X } from 'lucide-react';
import { cn } from '../../utils/cn';

const ToastCtx = createContext({ toast: () => {}, dismiss: () => {} });
export const useToast = () => useContext(ToastCtx);

const VARIANTS = {
  info:    { cls: 'bg-info-bg text-info border-info/30',         Icon: Info },
  success: { cls: 'bg-success-bg text-success border-success/30', Icon: CheckCircle2 },
  warn:    { cls: 'bg-warn-bg text-warn border-warn/30',         Icon: AlertTriangle },
  danger:  { cls: 'bg-danger-bg text-danger border-danger/30',   Icon: AlertCircle },
  neutral: { cls: 'bg-ink text-cream border-ink-3',              Icon: Info },
};

let _id = 0;

export function ToastProvider({ children, position = 'bottom-right', duration = 4000 }) {
  const [items, setItems] = useState([]);

  const dismiss = useCallback((id) => setItems((arr) => arr.filter(x => x.id !== id)), []);

  const toast = useCallback((opts) => {
    const id = ++_id;
    const item = { id, variant: 'neutral', duration, ...opts };
    setItems((arr) => [...arr, item]);
    if (item.duration > 0) setTimeout(() => dismiss(id), item.duration);
    return id;
  }, [dismiss, duration]);

  const posCls = {
    'top-right':    'top-4 right-4',
    'top-left':     'top-4 left-4',
    'top-center':   'top-4 left-1/2 -translate-x-1/2',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left':  'bottom-4 left-4',
    'bottom-center':'bottom-4 left-1/2 -translate-x-1/2',
  }[position];

  return (
    <ToastCtx.Provider value={{ toast, dismiss }}>
      {children}
      <div className={cn('fixed z-toast flex flex-col gap-2 max-w-[360px] w-[calc(100%-2rem)]', posCls)} aria-live="polite" aria-atomic="false">
        {items.map((t) => <ToastItem key={t.id} item={t} onClose={() => dismiss(t.id)} />)}
      </div>
    </ToastCtx.Provider>
  );
}

function ToastItem({ item, onClose }) {
  const { cls, Icon } = VARIANTS[item.variant] || VARIANTS.neutral;
  return (
    <div role="status" className={cn('flex items-start gap-3 border rounded-lg p-3 shadow-lg animate-slide-up', cls)}>
      <Icon className="w-[18px] h-[18px] shrink-0 mt-0.5" strokeWidth={1.8} />
      <div className="flex-1 leading-tight">
        {item.title && <div className="text-sm font-semibold">{item.title}</div>}
        {item.description && <div className={cn('text-sm leading-relaxed', item.title && 'mt-0.5 opacity-90')}>{item.description}</div>}
        {item.action && <div className="mt-2">{item.action}</div>}
      </div>
      <button onClick={onClose} aria-label="Fermer" className="shrink-0 -mr-1 -mt-1 p-1 rounded hover:bg-text/10">
        <X className="w-3.5 h-3.5" strokeWidth={2} />
      </button>
    </div>
  );
}

export default ToastProvider;
